import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { useBankLookupQuery } from '@/api/v1';
import { AddressFields } from '@/components/organisms/AddressFields';
import { ControllerWithError, ControllerWithErrorParam } from '@/forms/ControllerWithError';
import { FormWithErrorsRegister } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import type { paymentMethodFiatSchema } from '@/schemas/paymentMethod';
import { isEmptyNullOrUndefined } from '@/utility/isEmptyNullOrUndefined';

type PaymentMethodForm = typeof paymentMethodFiatSchema.infer;

interface Props {
  readonly register: FormWithErrorsRegister<PaymentMethodForm>;
  readonly control: Control<PaymentMethodForm>;
  readonly watch: UseFormWatch<PaymentMethodForm>;
  readonly setValue: UseFormSetValue<PaymentMethodForm>;
}

export const USDAccountFields: React.FC<Props> = (props: Props): React.ReactElement => {
  const { register, setValue, watch, control } = props;
  const [routingNumber, paymentMethod, country, state] = watch([
    'routingNumber',
    'paymentMethod',
    'country',
    'address.state',
  ]);

  const { data: bankData } = useBankLookupQuery(
    {
      routingNumber,
      paymentType: paymentMethod,
    },
    { skip: isEmptyNullOrUndefined(routingNumber) },
  );

  const tr = useTranslator();

  React.useEffect((): void => {
    if (bankData) {
      const { name, street, addressFull, state, city, zip } = bankData.data;
      setValue('bankName', name ?? '', { shouldTouch: true });
      setValue(
        'address',
        {
          street: street || addressFull || '',
          state: state ?? '',
          city: city ?? '',
          complement: '',
          zipCode: zip ?? '',
        },
        { shouldTouch: true },
      );
    }
  }, [bankData, setValue]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box mt={3}>
        <Typography variant="body2" color="text.primary">
          {tr('BANK_ACCOUNT_INFORMATION')}
        </Typography>
      </Box>
      <Box display="flex" gap={2}>
        <TextField
          fullWidth={true}
          {...register({ name: 'accountNumber', label: tr('ACCOUNT_NUMBER') })}
        />
        <TextField
          fullWidth={true}
          {...register({ name: 'routingNumber', label: tr('ROUTING_NUMBER') })}
        />
      </Box>
      <ControllerWithError
        control={control}
        name="bankName"
        label={tr('BANK_NAME')}
        render={({
          field,
        }: ControllerWithErrorParam<PaymentMethodForm, 'bankName'>): React.ReactElement => (
          <TextField fullWidth={true} {...field} />
        )}
      />

      <AddressFields<PaymentMethodForm>
        country={country}
        state={state}
        control={control}
        setValue={setValue}
      />
    </Box>
  );
};
